// src/hooks/usePickState.js
//
// Shared picks-state logic for the two screens that let someone enter
// picks against a pool entry: PickSubmission (a participant editing
// their own entry) and AdminPickEntry (an admin editing any
// participant's entry).
//
// The hook owns the work that's genuinely identical between those two
// screens:
//
//   - turning a flat games array into rounds, then into picks groups
//     via buildGroups()
//   - tracking which group is active and which rounds are collapsed
//     (with the auto-collapse rules)
//   - fetching the entry's saved picks
//   - the optimistic POST-and-update on each pick
//   - the derived counts the summary banner and tab badges need
//
// What it does NOT own: how the caller obtained `games` and `poolType`.
// AdminPickEntry fetches games once per tournament and reads pool_type
// off a selected pool; PickSubmission re-fetches games per active entry
// and reads pool_type off the entry. Those two policies are genuinely
// different, so each page keeps its own fetching code and just hands
// games and poolType to the hook.
//
// Inputs (object):
//   - activeEntry  : the currently-selected entry, or null. Must have
//                    entry_id when present.
//   - games        : flat array of game objects for the tournament. May
//                    be empty while loading.
//   - poolType     : 'bracket' | 'round_by_round' | etc., consumed by
//                    buildGroups(). May be null while loading.
//   - onFeedback   : function(message, type?) called after each pick
//                    save -- 'success' on success, 'error' on failure.
//   - autoOpenFirstScheduledGroup : when true (PickSubmission's
//                    behaviour) the hook picks the first group with a
//                    scheduled game as the initial activeGroup, so a
//                    participant lands on a useful tab. When false
//                    (AdminPickEntry's behaviour) it just picks
//                    groups[0].
//   - collapseSingleRoundGroups : when true, even a group with one
//                    round is auto-collapsed (admin behaviour). When
//                    false, single-round groups are left expanded so
//                    the user is never stranded behind a collapsed
//                    round whose toggle isn't rendered.
//
// Returns:
//   - picks            : { [gameId]: pickRow }
//   - gameMap          : { [gameId]: gameRow } -- handy for PickCard
//   - groups           : output of buildGroups()
//   - activeGroup      : currently selected group
//   - setActiveGroup   : pass to tab onClick
//   - collapsedRounds  : Set of round_numbers currently collapsed
//   - toggleCollapse(roundNumber) : flip a round's collapsed state
//   - saving           : { [gameId]: boolean } -- per-game spinner flag
//   - handlePick(gameId, teamId, teamName, isPlaceholder) : optimistic
//                                                            save
//   - reset()          : clear picks/groups; call when activeEntry
//                        becomes null
//   - counts: { pickedCount, totalInGroup, scheduledInGroup,
//               isGroupLocked }

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getPicks, submitPick, getPickDistribution } from '../services/api';
import { buildGroups }                              from '../lib/pickGroups';

export function usePickState({
    activeEntry,
    games,
    poolType,
    onFeedback,
    autoOpenFirstScheduledGroup = false,
    collapseSingleRoundGroups   = false,
}) {
    const [picks,           setPicks]           = useState({});
    const [saving,          setSaving]          = useState({});
    const [groups,          setGroups]          = useState([]);
    const [activeGroup,     setActiveGroup]     = useState(null);
    const [collapsedRounds, setCollapsedRounds] = useState(new Set());
    const [distribution,    setDistribution]    = useState({});

    // gameMap is a pure derivation from games -- memoize so PickCard
    // doesn't get a fresh object every render.
    const gameMap = useMemo(() => {
        const m = {};
        games.forEach(g => { m[g.id] = g; });
        return m;
    }, [games]);

    // Build groups whenever games or pool type changes. The original
    // pages both shaped roundMap with the same five fields off the game
    // rows; we keep that contract.
    useEffect(() => {
        if (games.length === 0 || !poolType) {
            setGroups([]);
            setActiveGroup(null);
            return;
        }
        const roundMap = {};
        games.forEach(g => {
            if (!roundMap[g.round_number]) {
                roundMap[g.round_number] = {
                    round_number:  g.round_number,
                    round_name:    g.round_name,
                    pick_deadline: g.pick_deadline,
                    pick_group:    g.pick_group,
                    games:         [],
                };
            }
            roundMap[g.round_number].games.push(g);
        });
        const roundList = Object.values(roundMap)
            .sort((a, b) => a.round_number - b.round_number);
        const built = buildGroups(roundList, poolType);
        setGroups(built);

        // Pick an initial active group. Two strategies, matching the
        // two callers' historical behaviour.
        let initial = null;
        if (autoOpenFirstScheduledGroup) {
            initial = built.find(g =>
                g.rounds.some(r =>
                    r.games.some(game => game.status === 'scheduled')
                )
            ) || built[0] || null;
        } else {
            initial = built[0] || null;
        }
        setActiveGroup(initial);
    }, [games, poolType, autoOpenFirstScheduledGroup]);

    // Auto-collapse rounds when the active group changes. AdminPickEntry
    // historically collapsed everything; PickSubmission skipped this for
    // single-round groups so the user wasn't trapped behind a collapsed
    // header whose toggle isn't rendered (CollapsibleRound suppresses
    // the toggle when collapsible={false}).
    useEffect(() => {
        if (!activeGroup) return;
        if (!collapseSingleRoundGroups && activeGroup.rounds.length <= 1) {
            setCollapsedRounds(new Set());
            return;
        }
        setCollapsedRounds(new Set(activeGroup.rounds.map(r => r.round_number)));
    }, [activeGroup, collapseSingleRoundGroups]);

    // Fetch picks when the active entry changes. When activeEntry goes
    // null (e.g. the admin clears the participant selection), we clear
    // picks so the previous entry's data doesn't leak through.
    useEffect(() => {
        if (!activeEntry) {
            setPicks({});
            return;
        }
        getPicks(activeEntry.entry_id).then(res => {
            const pickMap = {};
            res.data.forEach(pk => { pickMap[pk.game_id] = pk; });
            setPicks(pickMap);
        });
    }, [activeEntry]);

    const toggleCollapse = useCallback((roundNumber) => {
        setCollapsedRounds(prev => {
            const next = new Set(prev);
            if (next.has(roundNumber)) next.delete(roundNumber);
            else next.add(roundNumber);
            return next;
        });
    }, []);

    const handlePick = useCallback(async (gameId, teamId, teamName, isPlaceholder) => {
        if (!activeEntry) return;
        setSaving(prev => ({ ...prev, [gameId]: true }));
        try {
            await submitPick(activeEntry.entry_id, gameId, teamId);
            setPicks(prev => ({
                ...prev,
                [gameId]: {
                    ...prev[gameId],
                    game_id:             gameId,
                    picked_team_id:      isPlaceholder ? null : teamId,
                    source_game_pick_id: isPlaceholder ? teamId : null,
                    picked_team:         teamName,
                },
            }));
            if (onFeedback) onFeedback('Pick saved', 'success');
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to save pick';
            if (onFeedback) onFeedback(msg, 'error');
        } finally {
            setSaving(prev => ({ ...prev, [gameId]: false }));
        }
    }, [activeEntry, onFeedback]);

    const reset = useCallback(() => {
        setPicks({});
        setGroups([]);
        setActiveGroup(null);
    }, []);

    // Derived counts both pages need for their summary banner and tab
    // badges. We count picks over ALL games in the active group, not
    // just scheduled ones, so missed picks on already-final games stay
    // visible. isGroupLocked matches the original logic: there are
    // scheduled games, and none of them yet have both teams assigned.
    const counts = useMemo(() => {
        const activeGames    = activeGroup
            ? activeGroup.rounds.flatMap(r => r.games)
            : [];
        const scheduledGames = activeGames.filter(g => g.status === 'scheduled');
        const isGroupLocked  =
            scheduledGames.length > 0 &&
            scheduledGames.every(g => !g.team1_id && !g.team2_id);
        const pickedCount    = activeGames.filter(g => picks[g.id]).length;
        return {
            pickedCount,
            totalInGroup:     activeGames.length,
            scheduledInGroup: scheduledGames.length,
            isGroupLocked,
        };
    }, [activeGroup, picks]);

    // Effective deadline for the active group: MIN pick_deadline across
    // all rounds in the group. Grouped rounds (e.g. R1+R2) share a
    // single deadline window; ungrouped rounds use their own. Returns
    // null if no deadlines are set on any round in the group.
    const effectiveDeadline = useMemo(() => {
        if (!activeGroup) return null;
        const deadlines = activeGroup.rounds
            .map(r => r.pick_deadline)
            .filter(Boolean)
            .map(d => new Date(d).getTime());
        if (deadlines.length === 0) return null;
        return new Date(Math.min(...deadlines)).toISOString();
    }, [activeGroup]);

    // Fetch pick distribution for all games in the active group once the
    // deadline has passed. Fires on group change and when deadline flips.
    // Clears on group change so stale data from a prior group isn't shown
    // briefly before the new fetch lands.
    useEffect(() => {
        setDistribution({});
        if (!activeGroup || !activeEntry || !effectiveDeadline) return;
        if (new Date() <= new Date(effectiveDeadline)) return;
        const gameIds = activeGroup.rounds
            .flatMap(r => r.games)
            .map(g => g.id);
        if (gameIds.length === 0) return;
        getPickDistribution(activeEntry.pool_id, gameIds)
            .then(res => setDistribution(res.data))
            .catch(() => {}); // non-critical; silently ignore failures
    }, [activeGroup, activeEntry, effectiveDeadline]);

    return {
        picks,
        gameMap,
        groups,
        activeGroup,
        setActiveGroup,
        collapsedRounds,
        toggleCollapse,
        saving,
        handlePick,
        reset,
        counts,
        effectiveDeadline,
        distribution,
    };
}